/**
 * Token Expiration Monitor
 * Monitors JWT token expiration and triggers session expired event
 */

let expirationTimer = null;

/**
 * Decode JWT token to get expiration time
 * @param {string} token - JWT token
 * @returns {number|null} - Expiration timestamp in milliseconds or null
 */
const decodeTokenExpiration = (token) => {
  try {
    if (!token) return null;
    
    // JWT tokens have 3 parts separated by dots
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    // Decode the payload (second part)
    const payload = JSON.parse(atob(parts[1]));
    
    // JWT exp is in seconds, convert to milliseconds
    return payload.exp ? payload.exp * 1000 : null;
  } catch (error) {
    console.error('❌ Failed to decode token:', error);
    return null;
  }
};

/**
 * Check if token is expired
 * @param {string} token - JWT token
 * @returns {boolean} - True if expired
 */
export const isTokenExpired = (token) => {
  const expirationTime = decodeTokenExpiration(token);
  if (!expirationTime) return true;
  
  return Date.now() >= expirationTime;
};

/**
 * Get time until token expires in milliseconds
 * @param {string} token - JWT token
 * @returns {number} - Milliseconds until expiration (0 if expired)
 */
export const getTimeUntilExpiration = (token) => {
  const expirationTime = decodeTokenExpiration(token);
  if (!expirationTime) return 0;
  
  const timeLeft = expirationTime - Date.now();
  return Math.max(0, timeLeft);
};

/**
 * Start monitoring token expiration
 * @param {string} token - JWT token to monitor
 * @param {Function} onExpired - Callback when token expires
 */
export const startTokenExpirationMonitor = (token, onExpired) => {
  // Clear any existing timer
  stopTokenExpirationMonitor();
  
  if (!token) {
    console.warn('⚠️ No token provided to monitor');
    return;
  }
  
  const timeUntilExpiration = getTimeUntilExpiration(token);
  
  if (timeUntilExpiration === 0) {
    console.log('🔐 Token already expired');
    onExpired();
    return;
  }
  
  console.log(`⏰ Token will expire in ${Math.floor(timeUntilExpiration / 1000)} seconds`);
  
  // Set timer to trigger callback when token expires
  expirationTimer = setTimeout(() => {
    console.log('🔐 Token expired - triggering session expiration');
    onExpired();
  }, timeUntilExpiration);
};

/**
 * Stop monitoring token expiration
 */
export const stopTokenExpirationMonitor = () => {
  if (expirationTimer) {
    clearTimeout(expirationTimer);
    expirationTimer = null;
    console.log('🛑 Token expiration monitor stopped');
  }
};

/**
 * Check if token is about to expire (within 5 minutes)
 * @param {string} token - JWT token
 * @returns {boolean} - True if expiring soon
 */
export const isTokenExpiringSoon = (token) => {
  const timeUntilExpiration = getTimeUntilExpiration(token);
  const fiveMinutes = 5 * 60 * 1000;
  
  return timeUntilExpiration > 0 && timeUntilExpiration <= fiveMinutes;
};
